"use strict";

import notify from "./notify";

type DevConsoleModule = typeof import("./devConsole");
type AnyRecord = Record<string, any>;
const FORCE_OFFLINE_MODE_KEY = "krk_force_offline_sandbox";

function shouldLoadDevConsole(pathname = location.pathname): boolean {
  return pathname.endsWith("/") || pathname.endsWith("/index.html");
}

function readStoredForceOfflineMode(): boolean {
  try {
    const value = localStorage.getItem(FORCE_OFFLINE_MODE_KEY);
    return value == null ? true : value === "true" || value === "1";
  } catch {
    return true;
  }
}

function writeStoredForceOfflineMode(value: boolean): void {
  try {
    localStorage.setItem(FORCE_OFFLINE_MODE_KEY, String(value));
  } catch {}
}

function forceOfflineUrlIfEnabled(): void {
  const globals = window as typeof window & Record<string, any>;
  globals.forceOfflineMode = readStoredForceOfflineMode();
  if (!globals.forceOfflineMode || !shouldLoadDevConsole()) return;
  if (location.search === "?sandbox") return;
  history.replaceState(history.state, document.title, "/?sandbox");
}

forceOfflineUrlIfEnabled();

const devConsoleEnabledOnPage = shouldLoadDevConsole();
let devConsole: Pick<DevConsoleModule, "visible" | "toggleConsole"> = {
  visible: false,
  toggleConsole() {},
};

if (devConsoleEnabledOnPage) {
  devConsole = require("./devConsole.js");
}

export enum BhopMode {
  DISABLED = 0,
  SLIDEHOP = 1,
  HOLD_JUMP_CROUCH = 2,
  ALWAYS_JUMP = 3,
  HOLD_JUMP = 5,
}

const LEGACY_HOLD_JUMP_CROUCH_MODE = 4 as const;
export const BHOP_MODE_ORDER: BhopMode[] = [
  BhopMode.DISABLED,
  BhopMode.SLIDEHOP,
  BhopMode.HOLD_JUMP_CROUCH,
  BhopMode.ALWAYS_JUMP,
  BhopMode.HOLD_JUMP,
];
const isBhopMode = (value: number): value is BhopMode =>
  BHOP_MODE_ORDER.includes(value as BhopMode);

window.bhopMode = Number(localStorage.getItem("bhop"));
if (window.bhopMode === LEGACY_HOLD_JUMP_CROUCH_MODE) {
  window.bhopMode = BhopMode.HOLD_JUMP_CROUCH;
  localStorage.setItem("bhop", String(window.bhopMode));
}
if (!Number.isFinite(window.bhopMode) || !isBhopMode(window.bhopMode))
  window.bhopMode = BhopMode.DISABLED;
window.bhopEnabled = window.bhopMode !== BhopMode.DISABLED;
window.bhop = false;
export const BHOP_MODE_NAMES: Record<BhopMode, string> = {
  [BhopMode.DISABLED]: "Disabled",
  [BhopMode.SLIDEHOP]: "Slidehop (while moving)",
  [BhopMode.HOLD_JUMP_CROUCH]: "Hold Jump/Crouch",
  [BhopMode.ALWAYS_JUMP]: "Always Jump (while moving)",
  [BhopMode.HOLD_JUMP]: "Hold Jump",
};
window.ctrlTpEnabled = localStorage.getItem("ctrlTp") === "1";
window.ctrlTp = false;

let suppressNextSandboxNoclipEnable = false;
let sandboxNoclipInstallAttempts = 0;

function findSandboxLocalPlayer(globals: typeof window & AnyRecord): Krunker.Player | null {
  const socketId = globals.io?.socketId;
  const sandboxPlayers = globals.sandboxGame?.players;
  const candidate = (
    (socketId != null && typeof sandboxPlayers?.findById === "function" ? sandboxPlayers.findById(socketId) : null) ||
    (Array.isArray(sandboxPlayers?.list)
      ? sandboxPlayers.list.find((candidate: AnyRecord) => candidate && (candidate.isYou || candidate.id === socketId))
      : null) ||
    globals.sandboxPlayer ||
    globals.sandboxGame?.tmpPlayer ||
    null
  );
  return candidate as Krunker.Player | null;
}

function isSandboxRuntimeActive(globals: typeof window & AnyRecord): boolean {
  return !!(globals.Game?.isSandbox || globals.sandboxGame?.isSandbox);
}

function getCtrlTeleportPlayer(): Krunker.Player | null {
  const globals = window as typeof window & AnyRecord;
  if (isSandboxRuntimeActive(globals)) {
    return findSandboxLocalPlayer(globals);
  }
  return player || null;
}

function snapPlayerPosition(target: Krunker.Player, x: number, y: number, z: number, globals: typeof window & AnyRecord): void {
  target.x = x;
  target.y = y;
  target.z = z;
  target.fakeX = x;
  target.fakeY = y;
  target.fakeZ = z;
  target.forcePos = true;
  target.latestData = true;
  target.interpolate = false;
  if (Array.isArray((target as AnyRecord).posBuffer)) (target as AnyRecord).posBuffer.length = 0;
  target.velocity?.set?.(0, 0, 0);
  (target as AnyRecord).xVelC = 0;
  (target as AnyRecord).zVelC = 0;
  if ((target as AnyRecord).tmpVel) {
    (target as AnyRecord).tmpVel.x = 0;
    (target as AnyRecord).tmpVel.y = 0;
    (target as AnyRecord).tmpVel.z = 0;
  }
  globals.sandboxGame?.players?.resetMovVals?.(target);
  for (const obj of [(target as AnyRecord).objInstances, (target as AnyRecord).object, (target as AnyRecord).mesh]) {
    obj?.position?.set?.(x, y, z);
  }
}

function teleportSandboxLocalPlayer(target: Krunker.Player, x: number, y: number, z: number): void {
  const globals = window as typeof window & AnyRecord;
  const targets = new Set<Krunker.Player>();
  for (const candidate of [
    target,
    globals.sandboxPlayer,
    findSandboxLocalPlayer(globals),
    globals.tmpPlayer,
    globals.Game?.tmpPlayer,
    globals.player,
  ]) {
    if (candidate && typeof candidate === "object") targets.add(candidate as Krunker.Player);
  }
  for (const candidate of targets) snapPlayerPosition(candidate, x, y, z, globals);
  globals.sandboxPlayer = findSandboxLocalPlayer(globals) || target;
  const cameraPlayer = (globals.Game?.tmpPlayer || globals.tmpPlayer || globals.player || target) as Krunker.Player;
  const cameraHeight = Number(globals.config?.cameraHeight) || 4;
  const cameraY = cameraPlayer.y + (cameraPlayer.height || 11) - cameraHeight;
  globals.CONTROLS?.moveCam?.(cameraPlayer.x, cameraY, cameraPlayer.z);
  globals.Howler?.pos?.(cameraPlayer.x, cameraY, cameraPlayer.z);
}

function clearSandboxNoclipState(): void {
  const globals = window as typeof window & AnyRecord;
  const sandboxLocalPlayer = findSandboxLocalPlayer(globals);
  const candidates = [
    globals.sandboxPlayer,
    sandboxLocalPlayer,
    globals.player,
    globals.tmpPlayer,
    globals.Game?.tmpPlayer,
    globals.sandboxGame?.tmpPlayer,
  ];
  for (const candidate of candidates) {
    if (candidate) candidate.noclip = false;
  }
}

function sanitizeSandboxRawMapData(rawMapData: string): string {
  try {
    const mapData = JSON.parse(rawMapData) as AnyRecord;
    const scripts = mapData.scripts;
    if (!scripts || typeof scripts !== "object") return rawMapData;
    for (const key of Object.keys(scripts)) {
      const value = scripts[key];
      if (typeof value !== "string") continue;
      if (looksLikeKrunkScript(value)) {
        delete scripts[key];
        continue;
      }
      if (looksLikeBase64Script(value)) {
        try {
          const decoded = atob(value);
          if (looksLikeKrunkScript(decoded)) delete scripts[key];
        } catch {}
      }
    }
    if (!Object.keys(scripts).length) delete mapData.scripts;
    return JSON.stringify(mapData);
  } catch {
    return rawMapData;
  }
}

function sanitizeSandboxInitCustomMapData(customMapData: unknown): void {
  if (!customMapData || typeof customMapData !== "object") return;
  const mapData = customMapData as AnyRecord;
  if (typeof mapData.data !== "string") return;
  mapData.data = sanitizeSandboxRawMapData(mapData.data);
}

function looksLikeBase64Script(value: string): boolean {
  return value.length > 16 && value.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(value);
}

function looksLikeKrunkScript(value: string): boolean {
  return /(^|\n)\s*(#|public\s+action|action\s+|obj\s+|num\s+|str\s+|bool\s+)/.test(value);
}

function installSandboxNoclipReset(): void {
  const globals = window as typeof window & AnyRecord;
  const originalConnectToSandbox = globals.connectToSandbox as ((rawMapData: string) => void) & {
    __noNoclipOnSandbox?: boolean;
  };
  if (typeof originalConnectToSandbox !== "function") {
    if (sandboxNoclipInstallAttempts++ < 60) {
      window.setTimeout(installSandboxNoclipReset, 250);
    }
    return;
  }
  if (originalConnectToSandbox.__noNoclipOnSandbox) return;

  const wrappedConnectToSandbox = function (this: unknown, rawMapData: string): void {
    suppressNextSandboxNoclipEnable = true;
    installSandboxNoclipEventGuard();
    clearSandboxNoclipState();
    originalConnectToSandbox.call(this, sanitizeSandboxRawMapData(rawMapData));
    for (const delay of [0, 100, 500, 1000]) {
      window.setTimeout(() => {
        installSandboxNoclipEventGuard();
        clearSandboxNoclipState();
      }, delay);
    }
  };
  wrappedConnectToSandbox.__noNoclipOnSandbox = true;
  globals.connectToSandbox = wrappedConnectToSandbox;
}

function installSandboxNoclipEventGuard(): void {
  const globals = window as typeof window & AnyRecord;
  const events = globals.io?.events;
  const existingInit = events?.init as ((...args: unknown[]) => unknown) & {
    __sanitizeSandboxMapScripts?: boolean;
  };
  if (events && typeof existingInit === "function" && !existingInit.__sanitizeSandboxMapScripts) {
    const originalInit = existingInit;
    const sanitizedInit = function (this: unknown, ...args: unknown[]): unknown {
      if (globals.Game?.isSandbox) sanitizeSandboxInitCustomMapData(args[9]);
      return originalInit.apply(this, args);
    };
    sanitizedInit.__sanitizeSandboxMapScripts = true;
    events.init = sanitizedInit;
  }
  const existingNoclip = events?.noc as ((value: boolean, ...args: unknown[]) => unknown) & {
    __noAutoSandboxNoclip?: boolean;
  };
  if (!events || typeof existingNoclip !== "function" || existingNoclip.__noAutoSandboxNoclip) return;
  const originalNoclip = existingNoclip;
  const guardedNoclip = function (this: unknown, value: boolean, ...args: unknown[]): unknown {
    if (value === true && suppressNextSandboxNoclipEnable && globals.Game?.isSandbox) {
      suppressNextSandboxNoclipEnable = false;
      clearSandboxNoclipState();
      return undefined;
    }
    suppressNextSandboxNoclipEnable = false;
    return originalNoclip.call(this, value, ...args);
  };
  guardedNoclip.__noAutoSandboxNoclip = true;
  events.noc = guardedNoclip;
}

installSandboxNoclipReset();
window.setTimeout(installSandboxNoclipReset, 0);
window.setTimeout(installSandboxNoclipReset, 1000);
for (const delay of [0, 100, 500, 1000]) {
  window.setTimeout(installSandboxNoclipEventGuard, delay);
}

function readStoredValue(key: string): string | boolean | null {
  if (typeof window.getSavedVal === "function") {
    return window.getSavedVal(key);
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string): void {
  if (typeof window.saveVal === "function") {
    window.saveVal(key, value);
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function readStoredBool(key: string, fallback: boolean): boolean {
  const value = readStoredValue(key);
  if (value == null) return fallback;
  return value === true || value === "true" || value === "1";
}

function formatKeyBind(code: string): string {
  if (!code) return "Unbound";
  const special: Record<string, string> = {
    Backquote: "`",
    Minus: "-",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    Space: "Space",
    Escape: "Esc",
    ArrowUp: "Up",
    ArrowDown: "Down",
    ArrowLeft: "Left",
    ArrowRight: "Right",
  };
  if (special[code]) return special[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  return code
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/(Left|Right)$/, " $1");
}

window.devConsoleEnabled =
  devConsoleEnabledOnPage &&
  readStoredBool(
    "kro_setngss_devConsoleEnabled",
    // ["127.0.0.1", "localhost"].includes(location.hostname),
    true,
  );
{
  const storedBind = readStoredValue("krk_dev_console_bind");
  window.devConsoleKey =
    typeof storedBind === "string" ? storedBind : "Backquote";
}
{
  const storedBind = readStoredValue("krk_cycle_command_bind");
  window.cycleCommandKey = typeof storedBind === "string" ? storedBind : "F8";
}
{
  const storedBind = readStoredValue("krk_bhop_mode_bind");
  window.bhopModeKey = typeof storedBind === "string" ? storedBind : "KeyB";
}

let devConsoleCapturingKey = false;
let cycleCommandCapturingKey = false;
let bhopModeCapturingKey = false;

function refreshDevConsoleUi(): void {
  const offlineInput = document.querySelector<HTMLInputElement>(
    "[data-force-offline-setting] input[type='checkbox']",
  );
  if (offlineInput) offlineInput.checked = !!(window as typeof window & Record<string, any>).forceOfflineMode;

  const settingInput = document.querySelector<HTMLInputElement>(
    "[data-dev-console-setting] input[type='checkbox']",
  );
  if (settingInput) settingInput.checked = !!window.devConsoleEnabled;

  const bindValue = document.querySelector(
    "[data-dev-console-bind] .settText.floatRNoC.keyIcon",
  );
  if (bindValue) {
    bindValue.textContent = devConsoleCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.devConsoleKey);
  }

  const cycleBindValue = document.querySelector(
    "[data-cycle-command-bind] .settText.floatRNoC.keyIcon",
  );
  if (cycleBindValue) {
    cycleBindValue.textContent = cycleCommandCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.cycleCommandKey);
  }

  const bhopBindValue = document.querySelector(
    "[data-bhop-mode-bind] .settText.floatRNoC.keyIcon",
  );
  if (bhopBindValue) {
    bhopBindValue.textContent = bhopModeCapturingKey
      ? "Press any Key"
      : formatKeyBind(window.bhopModeKey);
  }
}

function setForceOfflineMode(value: boolean): void {
  const globals = window as typeof window & Record<string, any>;
  globals.forceOfflineMode = !!value;
  writeStoredForceOfflineMode(globals.forceOfflineMode);
  if (globals.forceOfflineMode) {
    forceOfflineUrlIfEnabled();
  } else if (location.search === "?sandbox") {
    history.replaceState(history.state, document.title, "/");
  }
  refreshDevConsoleUi();
}

function setDevConsoleEnabled(value: boolean): void {
  if (!devConsoleEnabledOnPage) return;
  window.devConsoleEnabled = !!value;
  writeStoredValue(
    "kro_setngss_devConsoleEnabled",
    String(window.devConsoleEnabled),
  );
  if (!window.devConsoleEnabled && devConsole.visible) {
    instructionHolder?.click?.();
    devConsole.toggleConsole();
  }
  refreshDevConsoleUi();
}

function setDevConsoleBind(code: string): void {
  window.devConsoleKey = code || "";
  writeStoredValue("krk_dev_console_bind", window.devConsoleKey);
  refreshDevConsoleUi();
}

function setCycleCommandBind(code: string): void {
  window.cycleCommandKey = code || "";
  writeStoredValue("krk_cycle_command_bind", window.cycleCommandKey);
  refreshDevConsoleUi();
}

function setBhopModeBind(code: string): void {
  window.bhopModeKey = code || "";
  writeStoredValue("krk_bhop_mode_bind", window.bhopModeKey);
  refreshDevConsoleUi();
}

function matchesCycleCommandBind(event: KeyboardEvent): boolean {
  return !!window.cycleCommandKey && event.code === window.cycleCommandKey;
}

function matchesBhopModeBind(event: KeyboardEvent): boolean {
  return !!window.bhopModeKey && event.code === window.bhopModeKey;
}

function toggleBhopMode(): void {
  {
    const currentMode = isBhopMode(window.bhopMode)
      ? window.bhopMode
      : BhopMode.DISABLED;
    const modeIndex = BHOP_MODE_ORDER.indexOf(currentMode);
    window.bhopMode = BHOP_MODE_ORDER[(modeIndex + 1) % BHOP_MODE_ORDER.length];
  }
  window.bhopEnabled = window.bhopMode !== BhopMode.DISABLED;
  localStorage.setItem("bhop", String(window.bhopMode));
  {
    const msg =
      "Bhop - " +
      BHOP_MODE_NAMES[
        isBhopMode(window.bhopMode) ? window.bhopMode : BhopMode.DISABLED
      ];
    // io_ch(null, msg, 2);
    notify(msg);
    console.log(msg);
  }
}

const PLAYER_LIST_WINDOW_ID = 23;
let lastPlayerListAltKeydownAt = 0;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
}

function isPlayerListKey(event: KeyboardEvent): boolean {
  return (
    event.key === "Alt" || event.code === "AltLeft" || event.code === "AltRight"
  );
}

function getElement(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  return el instanceof HTMLElement ? el : null;
}

function isPlayerListWindowOpen(): boolean {
  const holder = getElement("windowHolder");
  if (!holder || holder.style.display !== "block") return false;

  const header = getElement("windowHeader")?.textContent?.trim();
  const expectedHeader =
    window.windows?.[PLAYER_LIST_WINDOW_ID - 1]?.header?.trim();
  return !!header && !!expectedHeader && header === expectedHeader;
}

function closePlayerListWindow(returnToGame = false): void {
  if (!isPlayerListWindowOpen()) return;

  const showWindow = (
    window as unknown as {
      showWindow?: (windowId: number, forceRefresh?: boolean) => void;
    }
  ).showWindow;
  if (typeof showWindow !== "function") return;

  showWindow(PLAYER_LIST_WINDOW_ID);

  if (!returnToGame) return;
  window.setTimeout(() => {
    CONTROLS.resetKeys?.();
    CONTROLS.toggle?.(true);
  }, 0);
}

document.addEventListener(
  "keydown",
  (e) => {
    if (
      !devConsole.visible &&
      !isEditableTarget(e.target) &&
      isPlayerListKey(e)
    ) {
      lastPlayerListAltKeydownAt = Date.now();
    }

    if (e.key !== "Escape" || e.repeat || devConsole.visible) return;
    if (!isPlayerListWindowOpen()) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    closePlayerListWindow(true);
  },
  true,
);

window.addEventListener("blur", () => {
  if (Date.now() - lastPlayerListAltKeydownAt > 1500) return;
  window.setTimeout(() => closePlayerListWindow(false), 0);
});

function beginDevConsoleBindCapture(): void {
  if (!devConsoleEnabledOnPage) return;
  if (
    devConsoleCapturingKey ||
    cycleCommandCapturingKey ||
    bhopModeCapturingKey
  )
    return;
  devConsoleCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    devConsoleCapturingKey = false;
    if (event.code !== "Escape") {
      setDevConsoleBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function beginCycleCommandBindCapture(): void {
  if (
    devConsoleCapturingKey ||
    cycleCommandCapturingKey ||
    bhopModeCapturingKey
  )
    return;
  cycleCommandCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    cycleCommandCapturingKey = false;
    if (event.code !== "Escape") {
      setCycleCommandBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function beginBhopModeBindCapture(): void {
  if (
    devConsoleCapturingKey ||
    cycleCommandCapturingKey ||
    bhopModeCapturingKey
  )
    return;
  bhopModeCapturingKey = true;
  refreshDevConsoleUi();

  const captureKey = (event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    document.removeEventListener("keydown", captureKey, true);
    bhopModeCapturingKey = false;
    if (event.code !== "Escape") {
      setBhopModeBind(event.code);
    } else {
      refreshDevConsoleUi();
    }
  };

  document.addEventListener("keydown", captureKey, true);
}

function makeForceOfflineSettingRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.forceOfflineSetting = "1";
  row.title = "Rewrites the game URL to /?sandbox on load so the client starts offline.";
  row.append(document.createTextNode("Force Offline Mode "));

  const label = document.createElement("label");
  label.className = "switch";
  label.style.marginLeft = "10px";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = !!(window as typeof window & Record<string, any>).forceOfflineMode;
  input.addEventListener("click", () => setForceOfflineMode(input.checked));

  const slider = document.createElement("span");
  slider.className = "slider";

  label.append(input, slider);
  row.append(label);
  return row;
}

function makeDevConsoleSettingRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.devConsoleSetting = "1";
  row.title = "Allows opening the developer console in-game";
  row.append(document.createTextNode("Enable Developer Console (`) "));

  const label = document.createElement("label");
  label.className = "switch";
  label.style.marginLeft = "10px";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = !!window.devConsoleEnabled;
  input.addEventListener("click", () => setDevConsoleEnabled(input.checked));

  const slider = document.createElement("span");
  slider.className = "slider";

  label.append(input, slider);
  row.append(label);
  return row;
}

function makeDevConsoleBindHeading(): HTMLDivElement {
  const heading = document.createElement("div");
  heading.className = "setHedBind";
  heading.dataset.devConsoleBindHead = "1";
  heading.textContent = "Developer Tools";
  return heading;
}

function makeDevConsoleBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.devConsoleBind = "1";
  row.append(document.createTextNode("Developer Console"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setDevConsoleBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.devConsoleKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginDevConsoleBindCapture);

  row.append(clear, bind);
  return row;
}

function makeCycleCommandBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.cycleCommandBind = "1";
  row.append(document.createTextNode("Cycle Map (/cycle)"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setCycleCommandBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.cycleCommandKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginCycleCommandBindCapture);

  row.append(clear, bind);
  return row;
}

function makeBhopModeBindRow(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "settName";
  row.dataset.bhopModeBind = "1";
  row.append(document.createTextNode("Bhop Mode"));

  const clear = document.createElement("span");
  clear.className = "unbind";
  clear.addEventListener("click", () => setBhopModeBind(""));

  const clearIcon = document.createElement("i");
  clearIcon.className = "material-icons";
  clearIcon.style.fontSize = "40px";
  clearIcon.style.color = "var(--red)";
  clearIcon.textContent = "delete_forever";
  clear.append(clearIcon);

  const bind = document.createElement("span");
  bind.className = "settText floatRNoC keyIcon";
  bind.textContent = formatKeyBind(window.bhopModeKey);
  bind.addEventListener("mouseover", () => window.playTick?.());
  bind.addEventListener("click", beginBhopModeBindCapture);

  row.append(clear, bind);
  return row;
}

function ensureDevConsoleSettingRow(): void {
  if (!devConsoleEnabledOnPage) return;
  const anchorInput = document.getElementById("slid_flickClamp");
  if (!anchorInput) return;
  const anchorRow = anchorInput.closest(".settName");
  if (!anchorRow) return;

  let offlineRow = document.querySelector("[data-force-offline-setting='1']");
  if (!offlineRow) {
    offlineRow = makeForceOfflineSettingRow();
    anchorRow.insertAdjacentElement("afterend", offlineRow);
  } else if (offlineRow.previousElementSibling !== anchorRow) {
    offlineRow.remove();
    anchorRow.insertAdjacentElement("afterend", offlineRow);
  }

  let row = document.querySelector("[data-dev-console-setting='1']");
  if (!row) {
    row = makeDevConsoleSettingRow();
    offlineRow.insertAdjacentElement("afterend", row);
  } else if (row.previousElementSibling !== offlineRow) {
    row.remove();
    offlineRow.insertAdjacentElement("afterend", row);
  }

  refreshDevConsoleUi();
}

function ensureDevConsoleBindRow(): void {
  if (!devConsoleEnabledOnPage) return;
  const anchor =
    document.getElementById("cont37") || document.getElementById("cont34");
  if (!anchor) return;
  const anchorRow = anchor.closest(".settName");
  if (!anchorRow) return;

  let heading = document.querySelector("[data-dev-console-bind-head='1']");
  let devConsoleRow = document.querySelector("[data-dev-console-bind='1']");
  let bhopModeRow = document.querySelector("[data-bhop-mode-bind='1']");
  let cycleCommandRow = document.querySelector("[data-cycle-command-bind='1']");
  if (!heading || !devConsoleRow || !bhopModeRow || !cycleCommandRow) {
    heading?.remove();
    devConsoleRow?.remove();
    bhopModeRow?.remove();
    cycleCommandRow?.remove();
    heading = makeDevConsoleBindHeading();
    devConsoleRow = makeDevConsoleBindRow();
    bhopModeRow = makeBhopModeBindRow();
    cycleCommandRow = makeCycleCommandBindRow();
    anchorRow.insertAdjacentElement("afterend", heading);
    heading.insertAdjacentElement("afterend", devConsoleRow);
    devConsoleRow.insertAdjacentElement("afterend", bhopModeRow);
    bhopModeRow.insertAdjacentElement("afterend", cycleCommandRow);
  } else if (
    heading.previousElementSibling !== anchorRow ||
    devConsoleRow.previousElementSibling !== heading ||
    bhopModeRow.previousElementSibling !== devConsoleRow ||
    cycleCommandRow.previousElementSibling !== bhopModeRow
  ) {
    heading.remove();
    devConsoleRow.remove();
    bhopModeRow.remove();
    cycleCommandRow.remove();
    anchorRow.insertAdjacentElement("afterend", heading);
    heading.insertAdjacentElement("afterend", devConsoleRow);
    devConsoleRow.insertAdjacentElement("afterend", bhopModeRow);
    bhopModeRow.insertAdjacentElement("afterend", cycleCommandRow);
  }

  refreshDevConsoleUi();
}

function syncDevConsoleInterface(): void {
  ensureDevConsoleSettingRow();
  ensureDevConsoleBindRow();
}

{
  let scheduled = false;
  const scheduleSync = (): void => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      syncDevConsoleInterface();
    });
  };

  const startObserver = (): void => {
    const root = document.body || document.documentElement;
    if (!root) {
      requestAnimationFrame(startObserver);
      return;
    }
    new MutationObserver(scheduleSync).observe(root, {
      childList: true,
      subtree: true,
    });
    scheduleSync();
  };

  startObserver();
}

document.addEventListener("keydown", (e) => {
  // Ignore OS auto-repeat so holding a key doesn't spam actions (e.g. ` cycling maps).
  if (e.repeat) return;
  const target = e.target as Element | null;
  const input = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";

  if (
    devConsoleEnabledOnPage &&
    !!window.devConsoleEnabled &&
    !!window.devConsoleKey &&
    e.code === window.devConsoleKey
  ) {
    // Allow the bind to close the console even when its own input is focused.
    if (input && !devConsole.visible) return;
    e.preventDefault();
    devConsole.toggleConsole();
    return;
  }

  if (!input && !devConsole.visible && matchesCycleCommandBind(e)) {
    e.preventDefault();
    window.io.send("cycle");
    return;
  }

  if (!input && !devConsole.visible && matchesBhopModeBind(e)) {
    e.preventDefault();
    toggleBhopMode();
    return;
  }

  switch (e.key) {
    case "F1":
      if (input) return;
      window.io.send("clearLog");
      console.clear();
      break;
    case "F4":
      if (input) return;
      location.href = "/";
      break;
    case "F5":
      location.reload();
      break;
    case "F6": {
      console.log("Creating editor window");
      // ipcRenderer.send("create-editor");
      // Stage the currently-loaded map so the editor's checkEdit()
      // (getSavedVal("mapToEdit") -> importMap) opens it instead of the
      // Test.json fallback. The live map record lives on the engine at
      // Game.map.maps[Game.mapIndex]; mirror io_editMap's saveVal write.
      try {
        const mapIndex = Game?.mapIndex;
        const current =
          Game?.map?.maps &&
          mapIndex != null &&
          Game.map.maps[mapIndex as number];
        if (current && typeof current === "object") {
          saveVal("mapToEdit", JSON.stringify(current));
        }
      } catch (err) {
        console.error("Failed to stage current map for editor", err);
      }
      open("editor.html");
      break;
    }
    case "T":
    case "t":
      if (input) return;
      window.ctrlTpEnabled = !window.ctrlTpEnabled;
      localStorage.setItem("ctrlTp", String(+window.ctrlTpEnabled));
      {
        const msg =
          "Ctrl TP - " + (window.ctrlTpEnabled ? "Enabled" : "Disabled");
        // io_ch(null, msg, 2);
        notify(msg);
        console.log(msg);
      }
      break;
  }
});

{
  const pr = Array.prototype;
  const { push } = pr;
  pr.push = function (this: unknown[], ...args: unknown[]): number {
    if (this === window.CONTROLS?.tmpInpts && Array.isArray(args[0])) {
      const input = args[0] as Krunker.InputFrame;
      patchScrollInput(input);
      patchInputs(input);
    }
    return push.apply(this, args as any[]);
  };
}

const iInputs = {
  frame: 0,
  /**
   * capped at 0.1-33
   * source: var mAimTime = Math.max(varForeignSurface, Math.min(argChurchSteam[1], argCompletelyNearby.dltMx)) / this.deltaDiv;
   */
  delta: 1,
  xDir: 2,
  yDir: 3,
  moveDir: 4,
  shoot: 5,
  scope: 6,
  jump: 7,
  reload: 8,
  crouch: 9,
  weaponScroll: 10,
  weaponSwap: 11,
  moveLock: 12,
} as const satisfies Record<string, keyof Krunker.InputFrame & number>;

type WheelCompatEvent = WheelEvent & {
  wheelDelta?: number;
  detail?: number;
};

const SCROLL_WHEEL_KEY_CODE = 20000;
let pendingWeaponScroll = 0;
let pendingWeaponScrollAt = 0;

function getControlsForScrollPatch(): Partial<Krunker.Controls> | null {
  return typeof CONTROLS === "undefined" ? null : CONTROLS || null;
}

function getWheelDelta(event: WheelCompatEvent): number {
  const rawDelta =
    typeof event.wheelDelta === "number"
      ? event.wheelDelta
      : typeof event.detail === "number" && event.detail
        ? -event.detail
        : -event.deltaY;
  return -Math.max(-1, Math.min(1, rawDelta || 0));
}

function canForceWheelWeaponSwap(
  controls: Partial<Krunker.Controls> | null,
): boolean {
  return (
    !!player?.active &&
    controls?.enabled === true &&
    controls.inputChanger == null &&
    !window.locked &&
    !window.spectating
  );
}

function clearWheelKeyState(controls: Partial<Krunker.Controls> | null): void {
  if (controls?.keys) delete controls.keys[SCROLL_WHEEL_KEY_CODE];
  if (controls?.didPressed) delete controls.didPressed[SCROLL_WHEEL_KEY_CODE];
}

function forceWheelWeaponSwap(delta: number): void {
  const controls = getControlsForScrollPatch();
  if (!delta || !canForceWheelWeaponSwap(controls)) return;

  controls.scrollToSwap = true;
  clearWheelKeyState(controls);
  pendingWeaponScroll = delta * (window.settings?.tmp?.scrollDirM ?? 1);
  pendingWeaponScrollAt =
    typeof performance === "undefined" ? Date.now() : performance.now();
}

function onWheelCapture(event: Event): void {
  forceWheelWeaponSwap(getWheelDelta(event as WheelCompatEvent));
}

function onWheelBubble(): void {
  const controls = getControlsForScrollPatch();
  if (pendingWeaponScroll) {
    if (controls) controls.scrollToSwap = true;
    clearWheelKeyState(controls);
  }
}

function installScrollWheelPatch(): void {
  const captureOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
  };
  const bubbleOptions: AddEventListenerOptions = {
    capture: false,
    passive: true,
  };
  window.addEventListener("wheel", onWheelCapture, captureOptions);
  window.addEventListener("mousewheel", onWheelCapture, captureOptions);
  window.addEventListener("DOMMouseScroll", onWheelCapture, captureOptions);
  window.addEventListener("wheel", onWheelBubble, bubbleOptions);
  window.addEventListener("mousewheel", onWheelBubble, bubbleOptions);
  window.addEventListener("DOMMouseScroll", onWheelBubble, bubbleOptions);
}

function patchScrollInput(inputs: Krunker.InputFrame): void {
  if (inputs[iInputs.weaponScroll] || !pendingWeaponScroll) return;
  const now =
    typeof performance === "undefined" ? Date.now() : performance.now();
  if (now - pendingWeaponScrollAt > 250) {
    pendingWeaponScroll = 0;
    return;
  }
  if (!canForceWheelWeaponSwap(getControlsForScrollPatch())) return;

  inputs[iInputs.weaponScroll] = pendingWeaponScroll;
  pendingWeaponScroll = 0;
}

installScrollWheelPatch();

let slideDidCrouch = false;
window.slideHold = false;

window.addEventListener("blur", (e) => {
  // bhop go boinggg
  // window.bhop = true;
  window.ctrlTp = false;
  window.slideHold = false;
});

window.addEventListener("keydown", (e) => {
  if (e.repeat || document.activeElement?.tagName === "INPUT") return;

  if (e.code === "Space") {
    window.bhop = true;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = true;
  } else if (e.code === "ControlLeft" && window.ctrlTpEnabled) {
    window.ctrlTp = true;
  }
});

window.addEventListener("keyup", (e) => {
  // NOTE: do NOT gate on activeElement === INPUT here. Pressing Esc to
  // open the menu often shifts focus to a chat/search input, and the
  // resulting keyup for Space would then be swallowed — leaving
  // window.bhop stuck at true and the game auto-jumping in the menu.
  // Releasing held-key state must always be honored.
  if (e.repeat) return;

  if (e.code === "Space") {
    window.bhop = false;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = false;
  } else if (e.code === "ControlLeft" && window.ctrlTpEnabled) {
    window.ctrlTp = false;
  }
});

// Safety net: when the pointer lock is released (Esc / menu open) the
// browser may also drop the in-flight keyup if focus jumped to an input
// before keyup arrived. Clear all held-key state to match.
document.addEventListener("pointerlockchange", () => {
  if (!document.pointerLockElement) {
    window.bhop = false;
    window.slideHold = false;
    window.ctrlTp = false;
  }
});

{
  const pr = EventTarget.prototype;
  const { addEventListener } = pr;
  pr.addEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (
      listener &&
      this instanceof HTMLCanvasElement &&
      (type === "mousedown" || type === "mouseup")
    ) {
      const wrappedListener = function (this: EventTarget, event: Event): void {
        const mouseEvent = event as MouseEvent;
        // Call the original listener
        if (window.ctrlTpEnabled && window.ctrlTp && mouseEvent.which === 1) {
          if (event.type === "mouseup") {
            // window.ctrlTp = false;
          } else {
            const localPlayer = getCtrlTeleportPlayer();
            if (!localPlayer?.active || !RENDER?.camera) return;
            const tpRaycaster = new window.THREE.Raycaster();
            tpRaycaster.far = 6000;

            tpRaycaster.setFromCamera(
              new window.THREE.Vector2(0, 0),
              RENDER.camera,
            );
            const hits = tpRaycaster
              .intersectObject(RENDER.scene, true)
              .filter(function (h: any): boolean {
                let o = h.object;
                if (!o || !o.visible) return false;
                // walk up parents to skip local player
                let p = o;
                while (p) {
                  if (p === ((localPlayer as AnyRecord).object || (localPlayer as AnyRecord).objInstances)) return false;
                  p = p.parent;
                }
                return true;
              });
            if (hits.length) {
              const pt = hits[0].point;
              const tx = pt.x;
              const ty = pt.y + (localPlayer.height || 11);
              const tz = pt.z;
              if (isSandboxRuntimeActive(window as typeof window & AnyRecord)) {
                teleportSandboxLocalPlayer(localPlayer, tx, ty, tz);
              } else {
                io.send("tp", tx, ty, tz);
              }
            }
          }
          return;
        }
        if (typeof listener === "function") listener.call(this, event);
        else listener.handleEvent(event);
      };
      return addEventListener.call(this, type, wrappedListener, options);
    }
    return addEventListener.call(this, type, listener, options);
  };
}

// zeroSome (-0.015..0.005) randomises the falling-velocity threshold
// at which we begin crouching for the next slide, so the slidehop
// cadence doesn't look frame-perfect.
function bhopRandom(min: number, max: number, isFloat = false): number {
  const v = Math.random() * (max - min) + min;
  return isFloat ? v : Math.floor(v);
}

function pickZeroSome(): number {
  return bhopRandom(-0.015, 0.005, true);
}

// value between -1 and 1 that determines the velocity to start slidehopping at
// positive = going down
// negative = still increasing in velocity
let zeroSome = pickZeroSome();
let nextZeroSome = pickZeroSome();
let didCrouch = false;
let bhopTimer = 0;
let bhopStart = 0;
let lastJump = 0;

const bhopDelay = 130;

function isBhoppable() {
  const wallJump = true; // sketchConfig.get("wallJump")
  return (
    (wallJump &&
      Game.classConfig[player.classIndex]?.wallJ &&
      player.wallJump &&
      player.onWall) ||
    player.onGround
  );
}

function patchInputs(inputs: Krunker.InputFrame): void {
  if (!player || player.noclip) return;

  const bhopMode = isBhopMode(window.bhopMode)
    ? window.bhopMode
    : BhopMode.DISABLED;
  const oldBhop = false; // sketchConfig.get("rampAccel");
  const autoSlide = bhopMode === BhopMode.SLIDEHOP;
  const autoJump = autoSlide || bhopMode === BhopMode.ALWAYS_JUMP;
  const bhop =
    bhopMode === BhopMode.HOLD_JUMP_CROUCH || bhopMode === BhopMode.HOLD_JUMP;
  const slidehop = bhopMode === BhopMode.HOLD_JUMP_CROUCH;

  if (oldBhop) {
    if ((autoSlide && inputs[iInputs.moveDir] !== -1) || inputs[iInputs.jump]) {
      inputs[iInputs.jump] = isBhoppable() ? lastJump : 0;
      lastJump ^= 1;
    }

    // if crouch is held, slidehop
    if (autoSlide || (slidehop && inputs[iInputs.crouch])) {
      const willCrouch = (player.velocity.y || 0) < 0;
      inputs[iInputs.crouch] = willCrouch ? 1 : 0;
      didCrouch = willCrouch;
    } else {
      didCrouch = false;
    }

    return;
  }

  if (
    (autoJump && inputs[iInputs.moveDir] !== -1) ||
    (bhop && inputs[iInputs.jump])
  ) {
    const now = Date.now();

    if (bhopTimer <= now) {
      bhopTimer = 0;
      bhopStart = 0;
    }

    // reload for a random amount of frames to simulate pressing it
    // set bhopping only as soon as we start holding the input down
    if (!bhopTimer && isBhoppable()) {
      bhopTimer = now + bhopDelay + bhopRandom(0, 20);
      bhopStart = inputs[iInputs.frame] + bhopRandom(1, 3);
      inputs[iInputs.jump] = 0;
    } else {
      const mustBhop = inputs[iInputs.frame] >= bhopStart && now < bhopTimer;
      inputs[iInputs.jump] = mustBhop ? 1 : 0;
    }
  } else {
    bhopTimer = 0;
    bhopStart = 0;
  }
  // if crouch is held, slidehop
  if (autoSlide || (slidehop && inputs[iInputs.crouch])) {
    if (player.onGround && !inputs[iInputs.jump] && inputs[iInputs.crouch]) {
      inputs[iInputs.crouch] = 1;
      didCrouch = true;
    } else {
      if (!didCrouch) zeroSome = nextZeroSome;

      // pick a new "zeroSome" everytime we slidehop
      // otherwise they will randomly start/stop crouching as zeroSome is recalculated
      // the users will appear to be relatively accurate with slidehopping
      const willCrouch =
        !player.onGround && (player.velocity.y || 0) < zeroSome;

      inputs[iInputs.crouch] = willCrouch ? 1 : 0;

      if (!didCrouch && willCrouch) nextZeroSome = pickZeroSome();

      didCrouch = willCrouch;
    }
  } else {
    didCrouch = false;
  }
}

window.addEventListener("blur", () => {
  const doHopsBoing = localStorage.boing;
  if (!doHopsBoing) window.bhop = false;
});
